import { ArrowRight, Sparkles, Heart, Activity, ShieldCheck } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import PhoneMockup from "@/components/silentscan/PhoneMockup";
import { useChatbot } from "@/context/ChatbotContext";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Masked line-by-line reveal — each line rises from behind a clip mask
const LINE_VARIANT = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: {
      duration: 0.9,
      delay: 0.15 + i * 0.09,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const MaskedLine = ({ children, index = 0, className = "" }) => (
  // The reveal mask stays on the OUTER span; any text styling passed in must
  // land on the INNER span that actually holds the glyphs. `text-gradient-cyan`
  // sets -webkit-text-fill-color: transparent and clips a gradient to the text
  // box - on the outer span there are no direct text nodes to paint, and the
  // transparent fill still inherits down, so the second headline line rendered
  // completely invisible.
  <span
    className="block overflow-hidden"
    style={{ paddingBottom: "0.1em" }}
  >
    <motion.span
      variants={LINE_VARIANT}
      custom={index}
      className={`block will-change-transform ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { openChatbot } = useChatbot();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const phoneY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      ref={heroRef}
      data-testid="hero-section"
      className="relative pt-36 pb-20 md:pt-44 md:pb-32 overflow-hidden"
    >
      {/* Background layers */}
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="absolute inset-0 grid-radial" />
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-[120px]"
      />
      <motion.div
        style={{ y: glowY }}
        className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#0B3D91]/30 blur-[120px]"
      />

      <motion.div
        initial="hidden"
        animate="show"
        className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center"
      >
        {/* Left */}
        <div className="lg:col-span-7">
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 12 },
              show: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.05 } },
            }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card"
          >
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-300">
              Preventive Health Intelligence
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.02]"
          >
            <MaskedLine index={0}>Your Personalized</MaskedLine>
            <MaskedLine index={1} className="text-gradient-cyan">
              Health Score (0–100).
            </MaskedLine>
          </h1>

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 12 },
              show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.55 } },
            }}
            data-testid="hero-subheading"
            className="mt-6 max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed"
          >
            An AI-assisted platform for{" "}
            <span className="text-white font-medium">preventive health awareness</span>{" "}
            and{" "}
            <span className="text-white font-medium">personalized cognitive rehabilitation</span>{" "}
            — assessment, adaptive training and longitudinal performance
            tracking. Not a substitute for a qualified clinician.
          </motion.p>

          <motion.div
            variants={{
              hidden: { opacity: 0, y: 12 },
              show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.7 } },
            }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-try-demo"
              onClick={openChatbot}
              className="btn-glow rounded-full px-7 py-3.5 font-medium text-white inline-flex items-center gap-2"
            >
              Try Demo <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/dashboard"
              data-testid="hero-open-dashboard"
              className="btn-outline-glass rounded-full px-7 py-3.5 font-medium text-white"
            >
              Open Dashboard
            </Link>
            <button
              data-testid="hero-learn-more"
              onClick={() => scrollTo("#about")}
              className="text-sm text-zinc-400 hover:text-white ml-1"
            >
              Learn more →
            </button>
          </motion.div>

          {/* Trust bar */}
          <motion.div
            variants={{
              hidden: { opacity: 0 },
              show: { opacity: 1, transition: { duration: 0.8, delay: 0.9 } },
            }}
            className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-4"
          >
            {[
              { icon: ShieldCheck, label: "Privacy-First AI" },
              { icon: Activity, label: "Real-time Analysis" },
              { icon: Heart, label: "Prevention Focused" },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2 text-sm text-zinc-500"
              >
                <item.icon className="w-4 h-4 text-cyan-400/80" />
                <span className="tracking-wide">{item.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right - Phone mockup with parallax */}
        <motion.div
          style={{ y: phoneY }}
          className="lg:col-span-5 relative flex justify-center"
        >
          <div className="animate-float">
            <PhoneMockup />
          </div>
          <div className="absolute inset-0 -z-10 flex items-center justify-center">
            <div className="w-[380px] h-[380px] rounded-full bg-cyan-500/20 blur-[100px]" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
